/**
 * Payment Management Service (Admin)
 *
 * Provides methods for system administrators to manage subscription payments
 */

import api from '../api';
import type { SubscriptionPayment } from '../../types/subscription';
import type {
  PaginatedResponse,
  PaymentListParams,
  MessageResponse,
} from '../../types/admin';

/**
 * Data for creating a new payment record
 */
export interface CreatePaymentData {
  account_id: number;
  subscription_plan_id: number;
  amount: number;
  payment_method: 'stripe' | 'manual' | 'crypto' | 'bank_transfer';
  status?: 'pending' | 'completed' | 'failed';
  paid_at: string;
  notes?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Response from creating a payment
 */
export interface PaymentResponse {
  message: string;
  payment: SubscriptionPayment;
}

/**
 * Get paginated list of payments with filters
 */
export async function getAllPayments(
  params?: PaymentListParams
): Promise<PaginatedResponse<SubscriptionPayment>> {
  const response = await api.get<PaginatedResponse<SubscriptionPayment>>(
    '/api/admin/payments',
    { params }
  );
  return response.data;
}

/**
 * Get single payment by ID
 */
export async function getPaymentById(id: number): Promise<SubscriptionPayment> {
  const response = await api.get<{ payment: SubscriptionPayment }>(
    `/api/admin/payments/${id}`
  );
  return response.data.payment;
}

/**
 * Create new payment record
 */
export async function createPayment(
  data: CreatePaymentData
): Promise<SubscriptionPayment> {
  const response = await api.post<PaymentResponse>('/api/admin/payments', data);
  return response.data.payment;
}

/**
 * Delete payment record
 */
export async function deletePayment(id: number): Promise<MessageResponse> {
  const response = await api.delete<MessageResponse>(`/api/admin/payments/${id}`);
  return response.data;
}

/**
 * Get unique payment methods from database
 */
export async function getPaymentMethods(): Promise<string[]> {
  const response = await api.get<{ methods: string[] }>('/api/admin/payments/methods');
  return response.data.methods;
}

/**
 * Payment management service object
 */
export const paymentManagementService = {
  getAllPayments,
  getPaymentById,
  createPayment,
  deletePayment,
  getPaymentMethods,
};
